import type { Ref } from 'vue'

import {
  useNotificationSettings,
  type GuildNotificationCapabilities,
  type GuildNotificationSettings,
  type NotificationSettingsApi,
} from './notificationSettings'
import { useNotificationLiveSync } from './notificationLiveSync'
import {
  useNotificationTimers,
  type GuildNotificationTimer,
  type GuildNotificationTimerStatusChannel,
  type NotificationTimersApi,
} from './notificationTimers'

export const NOTIFICATION_STORE_PUBLIC_API_KEYS = [
  'guildNotificationCapabilities',
  'guildNotificationSettings',
  'guildNotificationSettingsLoading',
  'guildNotificationSettingsError',
  'loadGuildNotificationSettings',
  'setGuildNotificationSettings',
  'testGuildNotificationSettings',
  'guildNotificationTimers',
  'guildNotificationTimerStatusChannel',
  'guildNotificationTimersLoading',
  'guildNotificationTimersError',
  'loadGuildNotificationTimers',
  'addGuildNotificationTimer',
  'modifyGuildNotificationTimer',
  'startGuildNotificationTimer',
  'stopGuildNotificationTimer',
  'removeGuildNotificationTimer',
  'setGuildNotificationTimerStatusChannel',
] as const

export interface NotificationStoreComposition {
  readonly guildNotificationCapabilities: Ref<GuildNotificationCapabilities | null>
  readonly guildNotificationSettings: Ref<GuildNotificationSettings | null>
  readonly guildNotificationSettingsLoading: Ref<boolean>
  readonly guildNotificationSettingsError: Ref<string | null>
  readonly loadGuildNotificationSettings: NotificationSettingsApi['load']
  readonly setGuildNotificationSettings: NotificationSettingsApi['save']
  readonly testGuildNotificationSettings: NotificationSettingsApi['test']
  readonly guildNotificationTimers: Ref<ReadonlyArray<GuildNotificationTimer>>
  readonly guildNotificationTimerStatusChannel: Ref<GuildNotificationTimerStatusChannel | null>
  readonly guildNotificationTimersLoading: Ref<boolean>
  readonly guildNotificationTimersError: Ref<string | null>
  readonly loadGuildNotificationTimers: NotificationTimersApi['load']
  readonly addGuildNotificationTimer: NotificationTimersApi['add']
  readonly modifyGuildNotificationTimer: NotificationTimersApi['modify']
  readonly startGuildNotificationTimer: NotificationTimersApi['start']
  readonly stopGuildNotificationTimer: NotificationTimersApi['stop']
  readonly removeGuildNotificationTimer: NotificationTimersApi['remove']
  readonly setGuildNotificationTimerStatusChannel: NotificationTimersApi['setStatusChannel']
}

export function useNotificationStoreComposition(): NotificationStoreComposition {
  const notificationSettings = useNotificationSettings()
  const notificationTimers = useNotificationTimers()
  useNotificationLiveSync(notificationSettings, notificationTimers)

  return {
    guildNotificationCapabilities: notificationSettings.capabilities,
    guildNotificationSettings: notificationSettings.settings,
    guildNotificationSettingsLoading: notificationSettings.loading,
    guildNotificationSettingsError: notificationSettings.error,
    loadGuildNotificationSettings: notificationSettings.load,
    setGuildNotificationSettings: notificationSettings.save,
    testGuildNotificationSettings: notificationSettings.test,
    guildNotificationTimers: notificationTimers.timers,
    guildNotificationTimerStatusChannel: notificationTimers.statusChannel,
    guildNotificationTimersLoading: notificationTimers.loading,
    guildNotificationTimersError: notificationTimers.error,
    loadGuildNotificationTimers: notificationTimers.load,
    addGuildNotificationTimer: notificationTimers.add,
    modifyGuildNotificationTimer: notificationTimers.modify,
    startGuildNotificationTimer: notificationTimers.start,
    stopGuildNotificationTimer: notificationTimers.stop,
    removeGuildNotificationTimer: notificationTimers.remove,
    setGuildNotificationTimerStatusChannel: notificationTimers.setStatusChannel,
  }
}
